/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import React from 'react';
import {
  FlexColumn, Text, PageTitle,
  CheckCircledIcon,
} from '@/shared/ui';
import Button from '@/shared/ui/components/basic/Button';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@context/authentication';
import { clearPersistedOnboarding } from '../../hooks/usePersistedOnboarding';
import {
  SubscriptionActivationScreen,
  useSubscriptionActivation,
} from '@features/subscription';

type ActivationProps = {
  agencyId?: string | null;
  agencyName?: string | null;
  planName?: string | null;
  planId?: string | null;
  isSoloBroker?: boolean;
  /** Seats bought, frozen at checkout. Absent on the solo product. */
  seats?: number;
  /** What was charged today, frozen at checkout. */
  totalPaid?: number;
  billingCycle?: 'monthly' | 'yearly';
  /** Free days the purchase started with, frozen at checkout. 0 or absent: charged today. */
  trialDays?: number | null;
  /** One entry per tier bought, titled, frozen at checkout. */
  lines?: Array<{ title: string; seats: number }> | null;
  /**
   * Auxiliary users the purchase brought, already counted. Stated here
   * because this screen is the receipt: the buyer has just paid and is
   * being told what they got, and free users they do not know about are
   * users they will not staff.
   */
  supportSeats?: number | null;
};

/**
 * When the subscription renews.
 *
 * Derived rather than read back: the subscription record is still settling
 * behind the webhook when this screen paints, and a date the customer can see
 * beats a dash that resolves a second later.
 *
 * On a free period the first charge is the end of it, whatever the cycle:
 * the cycle only decides the one after.
 *
 * @param cycle - The billing cycle bought.
 * @param trialDays - Free days the purchase started with.
 * @returns The next billing date.
 */
function nextBillingDate(cycle: 'monthly' | 'yearly' | undefined, trialDays: number): Date {
  const next = new Date();
  if (trialDays > 0) next.setDate(next.getDate() + trialDays);
  else if (cycle === 'yearly') next.setFullYear(next.getFullYear() + 1);
  else next.setMonth(next.getMonth() + 1);
  return next;
}

const ActivationWelcome: React.FC<ActivationProps> = ({
  agencyId, agencyName, planName, isSoloBroker, seats, totalPaid, billingCycle, lines,
  supportSeats = null, trialDays = null,
}) => {
  const freeDays = trialDays ?? 0;
  const { t, i18n } = useTranslation('@pages/onboarding');
  const auth = useAuth();
  // The spec greets the BUYER — the person — and the person is who is logged
  // in. The agency's name was standing in for it (and often arrived empty).
  const buyerName = (auth.user?.profile as { given_name?: string })?.given_name
    || agencyName || '';
  const navigate = useNavigate();

  const basePath = agencyId ? `/app/agencies/${agencyId}` : '/app/workspace';

  const handleNavigate = () => {
    clearPersistedOnboarding();
    navigate(basePath, { replace: true });
  };

  const handleInvite = () => {
    clearPersistedOnboarding();
    navigate(`${basePath}/team/invite`, { replace: true });
  };

  const money = (v: number) => new Intl.NumberFormat(i18n.language, {
    style: 'currency', currency: 'BRL', minimumFractionDigits: 2,
  }).format(v);

  // The lead names the buyer and the plan, and on an agency it says what the
  // seats are for — the next thing they have to do is fill them.
  // An agency's plan is the product, not one of its tiers: the lead says
  // "Imobiliária" and the card lists the tiers line by line.
  const leadPlan = isSoloBroker ? (planName || '') : t('activation.agencyPlanName');
  const lead = [
    buyerName
      ? t('activation.lead', { name: buyerName, plan: leadPlan })
      : t('activation.leadNoName', { plan: leadPlan }),
    isSoloBroker ? '' : t('activation.leadInvite'),
  ].filter(Boolean).join(' ');

  const rows: { label: string; value: string }[] = [
    {
      label: t('activation.rows.plan'),
      value: isSoloBroker ? (planName || '—') : t('activation.agencyPlanName'),
    },
    // One row per tier bought (§6): "Cadeiras {Plano}: n". The single total
    // only appears when the titled lines did not survive the redirect.
    ...(isSoloBroker
      ? []
      : (lines?.length
        ? lines.map((line) => ({
          label: t('activation.rows.seatsOf', { plan: line.title }),
          value: String(line.seats),
        }))
        : (seats == null ? [] : [{ label: t('activation.rows.seats'), value: String(seats) }]))),
    // Free, so it sits with what was bought rather than with what was paid.
    ...(isSoloBroker || !supportSeats
      ? []
      : [{
        label: t('activation.rows.supportSeats'),
        value: String(supportSeats),
      }]),
    ...(totalPaid == null
      ? []
      : [{ label: t('activation.rows.paidToday'), value: money(freeDays > 0 ? 0 : totalPaid) }]),
    {
      label: t('activation.rows.nextCharge'),
      value: nextBillingDate(billingCycle, freeDays).toLocaleDateString(i18n.language),
    },
  ];

  return (
    <FlexColumn className="w-full items-center px-(--space-4) py-(--space-8) md:py-(--space-12) pb-(--space-24) md:pb-(--space-12)">
      <div className="flex flex-col items-center text-center animate-[fadeInUp_0.6s_ease] w-full max-w-md">
        <div
          className="w-14 h-14 rounded-full flex items-center justify-center mb-(--space-5) animate-[scaleIn_0.4s_ease_0.2s_both]"
          style={{ background: 'var(--seed-base)', boxShadow: '0 8px 24px color-mix(in srgb, var(--seed-base) 15%, transparent)' }}
        >
          <CheckCircledIcon width={24} height={24} color="white" />
        </div>

        <PageTitle>{t('activation.title')}</PageTitle>
        <Text
          className="text-sm mt-(--space-2)"
          style={{ color: 'var(--text-secondary)', lineHeight: 1.6, textWrap: 'pretty' }}
        >
          {lead}
        </Text>

        <div
          className="w-full mt-(--space-6) flex flex-col gap-(--space-3) text-left"
          style={{
            padding: 'var(--space-5)', background: 'var(--surface-raised)',
            border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-xl)',
          }}
        >
          {rows.map((row) => (
            <div key={row.label} className="flex items-baseline justify-between gap-(--space-3)">
              <Text
                component="span"
                style={{ fontSize: 'var(--text-tag)', color: 'var(--text-secondary)' }}
              >
                {row.label}
              </Text>
              <Text
                component="span"
                style={{
                  fontSize: 'var(--text-small)', fontWeight: 'var(--font-semibold)',
                  color: 'var(--text-primary)',
                }}
              >
                {row.value}
              </Text>
            </div>
          ))}
        </div>

        <div className="w-full mt-(--space-6) flex flex-col-reverse sm:flex-row gap-(--space-3)">
          {!isSoloBroker && (
            <Button variant="outline" size="md" onClick={handleInvite} className="flex-1">
              {t('activation.inviteBrokers')}
            </Button>
          )}
          <Button variant="solid" size="md" onClick={handleNavigate} className="flex-1">
            {t('activation.goToHousien')}
          </Button>
        </div>

        <Text
          className="mt-(--space-5)"
          style={{ fontSize: 'var(--text-micro)', color: 'var(--text-tertiary)' }}
        >
          {t('activation.receiptNote')}
        </Text>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.8); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </FlexColumn>
  );
};

/**
 * Post-checkout activation gate.
 *
 * Reconciles the subscription with Payien before showing the welcome screen,
 * preventing the misleading "subscription required" overlay during the
 * webhook race window. Shows a confirmation screen while LOADING, a retry
 * affordance on TIMEOUT, and the welcome screen once the agency is active.
 */
const Activation: React.FC<ActivationProps> = (props) => {
  const { status, retry } = useSubscriptionActivation({
    agencyId: props.agencyId ?? '',
    enabled: Boolean(props.agencyId),
  });

  if (props.agencyId && status !== 'SUCCESS') {
    return (
      <SubscriptionActivationScreen
        status={status}
        agencyId={props.agencyId}
        onRetry={retry}
      />
    );
  }

  return <ActivationWelcome {...props} />;
};

export default Activation;
